import { useMutation, useQueryClient } from "@tanstack/react-query";

import { $api, fetchClient, invalidate } from "./client";
import type { components } from "./schema";

export type Source = components["schemas"]["Source"];
export type Component = components["schemas"]["Component"];
export type Flavor = components["schemas"]["FlavorEnum"];

/** Unpaginated: an item has a handful of sources across its locations. The
 *  mutations are bound to the item too; a source lives under what it produces. */
export const useItemSources = (item: number) =>
  $api.useQuery("get", "/api/v1/items/{item}/sources/", {
    params: { path: { item } },
  });

export interface ComponentInput {
  id?: number;
  item: number;
  location: number;
  quantity: string;
  consumeOffset: number;
}

export interface SourceInput {
  code: string;
  name: string;
  location: number;
  supplier: number | null;
  priority: number;
  leadTime: number;
  minimumQuantity: string | null;
  lotMultiple: number | null;
  components: ComponentInput[];
}

const invalidateSources = (queryClient: ReturnType<typeof useQueryClient>) =>
  invalidate(queryClient, "/api/v1/items/{item}/sources/");

export function useCreateSource(item: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SourceInput) =>
      (
        await fetchClient.POST("/api/v1/items/{item}/sources/", {
          params: { path: { item } },
          body: body as components["schemas"]["Source"],
        })
      ).data!,
    onSuccess: () => invalidateSources(queryClient),
  });
}

export function useUpdateSource(item: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: { id: number } & Partial<SourceInput>) =>
      (
        await fetchClient.PATCH("/api/v1/items/{item}/sources/{id}/", {
          params: { path: { item, id } },
          body: body as components["schemas"]["PatchedSourceRequest"],
        })
      ).data!,
    onSuccess: () => invalidateSources(queryClient),
  });
}

export function useDeleteSource(item: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      fetchClient.DELETE("/api/v1/items/{item}/sources/{id}/", {
        params: { path: { item, id } },
      }),
    onSuccess: () => invalidateSources(queryClient),
  });
}
